import { useState, useCallback, useEffect, useMemo } from 'react';
// @mui
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
import { RouterLink } from 'src/routes/components';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// types
import { VuonTrong } from 'src/types/vuontrong';
// components
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import {
  useTable,
  emptyRows,
  TableNoData,
  getComparator,
  TableEmptyRows,
  TableHeadCustom,
  // TableSelectedAction, // Đã loại bỏ
  TablePaginationCustom,
} from 'src/components/table';
// sections
import DarkModeButton from 'src/components/button/button-dark-model';
import useVuonTrong from '../hooks/use-vuontrong';
import VuonTrongTableRow from '../vuontrong-table-row';
import VuonTrongTableToolbar from '../vuontrong-table-toolbar';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'tenVuon', label: 'Tên vườn', width: 300 },
  { id: 'diaChi', label: 'Địa chỉ', width: 300 },
  { id: 'viTri', label: 'Vị trí', width: 200 },
  { id: 'trangThai', label: 'Trạng thái', width: 120, align: 'center' },
  { id: '', width: 88 },
];

const defaultFilters = {
  name: '',
  status: 0,
};

export default function ListVuonTrongView() {
  const table = useTable();
  const settings = useSettingsContext();
  const router = useRouter();
  const confirm = useBoolean(); 
  const [tableData, setTableData] = useState<VuonTrong[]>([]);
  const [filters, setFilters] = useState(defaultFilters);
  const { gardens, getVuonTrongs, loading, deleteVuonTrong } = useVuonTrong();


  const dataCounts = useMemo(() => ({
    total: tableData.length,
    active: tableData.filter(garden => garden.trangThai === 1).length,
    paused: tableData.filter(garden => garden.trangThai === 2).length,
    inactive: tableData.filter(garden => garden.trangThai === 3).length,
  }), [tableData]);

  const fetchData = useCallback(async () => {
    try {
      await getVuonTrongs();
    } catch (error) {
      console.error('Error fetching gardens:', error);
    }
  }, [getVuonTrongs]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (gardens && gardens.length > 0) {
      setTableData(gardens);
    }
  }, [gardens]);

  const dataFiltered = applyFilter({
    inputData: tableData,
    comparator: getComparator(table.order, table.orderBy),
    filters,
  });

  const dataInPage = dataFiltered.slice(
    table.page * table.rowsPerPage,
    table.page * table.rowsPerPage + table.rowsPerPage
  );

  const denseHeight = table.dense ? 52 : 72;

  const canReset = !!filters.name || filters.status !==  0;

  const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;

  const handleFilters = useCallback(
    (name: string, value: string) => {
      table.onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleDeleteRow = useCallback(
    (id: number) => {
      // Logic xóa đơn lẻ (giữ lại)
      const deleteRow = tableData.filter((row) => row.vuonTrong_ID !== id);
      setTableData(deleteRow);
      table.onUpdatePageDeleteRow(dataInPage.length);
    },
    [dataInPage.length, table, tableData]
  );

  // Loại bỏ hoàn toàn hàm handleDeleteRows (xóa nhiều)

  const handleEditRow = useCallback(
    (id: number) => {
      console.log('handleEditRow called with id:', id);
      router.push(paths.dashboard.vuontrong.edit(id));
    },
    [router]
  );

  const handleViewRow = useCallback(
    (id: number) => {
      router.push(paths.dashboard.vuontrong.edit(id));
    },
    [router]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Danh sách Vườn trồng"
        links={[
          { name: 'Dashboard', href: paths.dashboard.root },
          { name: 'Vườn trồng', href: paths.dashboard.vuontrong.root },
          { name: 'Danh sách' },
        ]}
        action={
         <DarkModeButton
          component={RouterLink}
          href={paths.dashboard.vuontrong.new}
          variant="contained"
          startIcon={<Iconify icon="mingcute:add-line" />}
        >
          Thêm Vườn trồng 
        </DarkModeButton>
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 360,
          }}
        >
          <CircularProgress />  
        </Box>
      ) : (
      <Card>
        <VuonTrongTableToolbar
          filters={filters}
          onFilters={handleFilters}
          onResetFilters={handleResetFilters}
          canReset={canReset}
          numSelected={0} // Luôn là 0 vì không cho phép chọn nhiều
          dataCounts={dataCounts} // Giữ lại để hiển thị số lượng trên Tabs
        /> 

        {/* Loại bỏ TableSelectedAction */}
        {/* {table.selected.length > 0 && (...) } */}

        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={table.order}
                orderBy={table.orderBy}
                headLabel={TABLE_HEAD}
                // Loại bỏ các props liên quan đến chọn/xóa nhiều
                // rowCount={tableData.length}
                // numSelected={table.selected.length}
                onSort={table.onSort}
                // onSelectAllRows bị loại bỏ
              />

              <TableBody>
                {dataInPage.map((row) => (
                  <VuonTrongTableRow
                    key={row.vuonTrong_ID}
                    row={row}
                    // Loại bỏ logic chọn hàng
                    // selected={table.selected.includes(String(row.vuonTrong_ID))}
                    // onSelectRow={() => table.onSelectRow(String(row.vuonTrong_ID))}
                    onDeleteRow={() => handleDeleteRow(row.vuonTrong_ID!)}
                    onEditRow={() => handleEditRow(row.vuonTrong_ID!)}
                    onViewRow={() => handleViewRow(row.vuonTrong_ID!)}
                  />
                ))}

                <TableEmptyRows
                  height={denseHeight}
                  emptyRows={emptyRows(table.page, table.rowsPerPage, tableData.length)}
                />

                <TableNoData notFound={notFound} content='Không tìm thấy dữ liệu' />
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={dataFiltered.length}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
      )}

    </Container>
  );
}

// ----------------------------------------------------------------------

function applyFilter({
  inputData,
  comparator,
  filters,
}: {
  inputData: VuonTrong[];
  comparator: (a: any, b: any) => number;
  filters: { name: string; status: number };
}) {
  const { name, status } = filters;

  const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  if (name) {
    inputData = inputData.filter((garden) =>
      garden.tenVuon?.toLowerCase().includes(name.toLowerCase()) ||
      garden.diaChi?.toLowerCase().includes(name.toLowerCase()) ||
      garden.viTri?.toLowerCase().includes(name.toLowerCase())
    );
  }

  if (status !== 0) {
    inputData = inputData.filter((garden) => {
      if (status === 1) return garden.trangThai === 1;
      if (status === 2) return garden.trangThai === 2;
      if (status === 3) return garden.trangThai === 3;
      return true;
    });
  }

  return inputData;
}